import { api } from "../lib/api";
import { DOC_TYPES, MEALS, fdate, fmt, money, run, useLoad } from "../lib/util";
import { Alert, Badge, Card, SectionTitle } from "../components/kit";
import { useSchool } from "./ctx";

export const OPENING = {
  nao_informado: ["Estoque anterior não informado", "red"],
  conferido: ["Estoque anterior conferido, aguardando gravação", "amber"],
  gravado: ["Estoque anterior gravado", "green"],
};

function ContractBalance({ contracts, onOpen }) {
  if (!contracts?.length) return null;
  return (
    <Card data-testid="overview-contract-balance">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-heading font-bold text-[#0B192C]">Agricultura familiar — falta receber</h3>
        <button className="text-sm text-[#0B192C] underline" data-testid="overview-contracts-open" onClick={onOpen}>Ver contratos</button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {contracts.map((c) => (
          <div key={c.id} className="rounded-lg bg-slate-50 p-3" data-testid={`overview-contract-${c.number}`}>
            <div className="flex justify-between gap-2">
              <div className="font-semibold text-sm">Contrato {c.number} · {c.supplier}</div>
              <Badge tone={c.items.length ? "amber" : "green"}>{c.items.length ? money(c.value_remaining) : "Concluído"}</Badge>
            </div>
            <div className="text-xs text-slate-500 mb-2">Vigência até {fdate(c.end_date)}</div>
            {c.items.length ? (
              <div className="max-h-40 overflow-auto text-xs space-y-1">
                {c.items.map((r) => (
                  <div key={r.product} className="flex justify-between"><span>{r.product}</span><span className="font-mono">{fmt(r.remaining)} a receber{r.pct != null && ` · ${fmt(r.pct)}% executado`}</span></div>
                ))}
              </div>
            ) : <div className="text-xs text-green-700">Todos os itens comprados foram recebidos.</div>}
          </div>
        ))}
      </div>
    </Card>
  );
}

function FlagRow({ f, i, onDone }) {
  const pick = async (v) => {
    if (await run(() => api.put(`/s/af-notes/${f.document_id}/items/${f.item_index}`, { contract_desc: v || null }), "Produto associado ao contrato")) onDone();
  };
  if (f.type !== "sem_correspondencia" || !f.options?.length || f.item_index == null) return <div data-testid={`contract-flag-${i}`}>{f.message}</div>;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`contract-flag-${i}`}>
      <span>{f.message}</span>
      <select data-testid={`contract-flag-select-${i}`} className="border rounded px-2 py-1 text-xs bg-white" defaultValue="" onChange={(e) => pick(e.target.value)}>
        <option value="">Corresponde a…</option>
        {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

export default function Overview() {
  const { month, tick, setSection } = useSchool();
  const [o, reload] = useLoad(async () => (await api.get("/s/overview", { params: { month } })).data, [month, tick]);
  if (!o) return <div className="text-slate-500">Carregando…</div>;
  const s = o.stock;
  const kpis = [["Gêneros no mês", o.totals.produtos], ["Entradas (un. estoque)", fmt(o.totals.entradas)], ["Saídas na base atual", fmt(o.totals.saidas)], ["Dias de atendimento", o.totals.dias_atendimento]];
  return (
    <div className="space-y-6">
      <SectionTitle title="Visão geral" subtitle="Situação do mês, pendências e inconsistências que precisam de atenção." />
      <div className="flex flex-wrap gap-3 items-center">
        <Badge tone={OPENING[s.opening_status][1]} data-testid="opening-status-badge">{OPENING[s.opening_status][0]}</Badge>
        {s.start_month && <span className="text-xs text-slate-500">Implantação: {s.start_month.slice(5)}/{s.start_month.slice(0, 4)}</span>}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map(([l, v]) => (
          <Card key={l} data-testid={`kpi-${l}`}>
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{l}</div>
            <div className="font-heading text-3xl font-bold text-[#0B192C] mt-1">{v}</div>
          </Card>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-5">
        <Card>
          <h3 className="font-heading font-bold text-[#0B192C] mb-3">Comensais por refeição (Modo IA)</h3>
          <div className="grid grid-cols-4 gap-3">
            {Object.entries(MEALS).map(([k, l]) => (
              <div key={k} className="rounded-lg bg-slate-50 p-3 text-center">
                <div className="text-xs text-slate-500">{l}</div>
                <div className="font-heading text-xl font-bold" data-testid={`meal-count-${k}`}>{s.meal_counts[k] ?? <Badge tone="amber">Pendente</Badge>}</div>
              </div>
            ))}
          </div>
        </Card>
        <Card>
          <h3 className="font-heading font-bold text-[#0B192C] mb-3">Documentos</h3>
          <div className="space-y-2 text-sm">
            {Object.entries(DOC_TYPES).map(([k, v]) => {
              const c = o.documents[k] || {};
              return (
                <button key={k} onClick={() => setSection(`doc:${k}`)} className="w-full flex justify-between items-center hover:bg-slate-50 rounded px-2 py-1">
                  <span>{v.n}. {v.short}</span>
                  <span className="flex gap-1.5">
                    {c.confirmado ? <Badge tone="green">{c.confirmado} confirmado(s)</Badge> : null}
                    {c.extraido ? <Badge tone="amber">{c.extraido} a conferir</Badge> : null}
                    {(c.erro || 0) + (c.duplicado || 0) ? <Badge tone="red">{(c.erro || 0) + (c.duplicado || 0)} com problema</Badge> : null}
                    {!Object.keys(c).length && <Badge>Nenhum</Badge>}
                  </span>
                </button>
              );
            })}
          </div>
        </Card>
      </div>
      <ContractBalance contracts={o.contracts} onOpen={() => setSection("agro")} />
      {s.negatives.length > 0 && (
        <Alert tone="red" title="Estoque negativo — correção obrigatória" testid="negatives-alert">
          {s.negatives.map((n) => <div key={n.product_id}>{n.name}: saldo final {fmt(n.final)}</div>)}
          <div className="mt-1 text-xs">Revise entradas, saídas ou registre um ajuste confirmado no Controle mensal. O PDF do mapa fica bloqueado até a correção.</div>
        </Alert>
      )}
      {o.pendencias.length > 0 && (
        <Alert title="Pendências" testid="pendencias-alert">
          <ul className="list-disc pl-5">{o.pendencias.map((p) => <li key={p}>{p}</li>)}</ul>
        </Alert>
      )}
      {s.no_prep.length > 0 && (
        <Alert tone="info" title="Produtos em estoque sem preparação compatível no cardápio">
          {s.no_prep.map((n) => <div key={n.product_id}>{n.name}: {fmt(n.final)} mantido em estoque</div>)}
        </Alert>
      )}
      {(s.substitutions || []).length > 0 && (
        <Alert tone="info" title={`Preparos feitos com substituição por falta do produto (${s.substitutions.length})`}>
          <div className="max-h-40 overflow-auto text-xs" data-testid="overview-substitutions">{s.substitutions.map((x, i) => <div key={i}>{x.date.slice(8)}/{x.date.slice(5, 7)} · {MEALS[x.meal]} · {x.preparation}: falta de {(x.items || []).map((y) => y.product + (y.to_product ? ` → usado ${y.to_product}` : "")).join(", ")}</div>)}</div>
        </Alert>
      )}
      {o.contract_flags.length > 0 && (
        <Alert tone="amber" title="Agricultura familiar — confira (não bloqueia o mês)">
          <div className="space-y-1">{o.contract_flags.map((f, i) => <FlagRow key={i} f={f} i={i} onDone={reload} />)}</div>
        </Alert>
      )}
    </div>
  );
}
